/**
 * Plan-aware wizard for capturing a FieldRecord at a single well.
 *
 * Phase 3.20: focused gauging form (DTW + water elevation + is_dry +
 * notes + GPS). Schema-driven generic renderer (photos / signatures
 * / sketches / probe auto-fill) is a follow-on; the seam exists in
 * @argos/form-schemas and the FieldDef.kind dispatcher will be added
 * when those field kinds are needed.
 *
 * Lifecycle:
 *   open                     → load plan item + cached well + draft
 *   user types               → autosave draft to local_field_records
 *   submit                   → enqueueSubmission to /events/{id}/field-records
 *   sync engine drains       → server upserts FieldRecord
 *   next refresh             → cached_field_records gets the synced row
 *
 * Validation: runs SCREEN_INTERVAL_CHECK from @argos/validation on the
 * DTW + cached well shape. Warnings render inline; never block submit.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type {
  AssignedEventEnvelope,
  FieldRecordUpsert,
  SamplingPlanItem,
} from '@argos/api-types';
import { validateRowOffline, type ValidationIssue } from '@argos/validation';
import { ArrowLeftIcon, GpsIcon } from '../components/icons';
import { getDb, type PhotoBlobRow, type CachedFlagCode } from '../db';
import { flagCodeLabel, dedupeFlagCodes } from '../lib/flagCode';
import { useGpsFix, gpsAccuracyBucket, haversineMeters } from '../hooks/useGpsFix';
import {
  capturePhoto,
  listPhotos,
  reassignPhotos,
  removePhoto,
  MAX_PHOTOS_PER_RECORD,
} from '../photos/capture';
import { enqueueSubmission } from '../sync/queue';
import * as haptics from '../lib/haptics';
import {
  stepsForWell,
  stepLabel,
  expandPlanItemSamples,
  type StepId,
  type WizardDraft,
} from './wizard/state';
import { Stepper } from './wizard/Stepper';
import { FieldParamsStep } from './wizard/FieldParamsStep';
import { PurgeStep } from './wizard/PurgeStep';
import { SamplesStep } from './wizard/SamplesStep';
import { ObservationsStep } from './wizard/ObservationsStep';
import { ReviewStep } from './wizard/ReviewStep';
import { isStabilized, type Reading } from '../lib/stabilization';
import type { SampleCollected } from './wizard/state';
import { ChevronLeft, ChevronRight, Send } from 'lucide-react';

interface CachedWellShape {
  id: string;
  name: string;
  latitude?: number | null;
  longitude?: number | null;
  total_depth_ft?: number | null;
  casing_elevation_ft?: number | null;
  measuring_point_elevation_ft?: number | null;
  screen_top_ft?: number | null;
  screen_bottom_ft?: number | null;
  geofence_radius_m?: number | null;
}

interface FormState {
  is_dry: boolean;
  depth_to_water_ft: string;
  water_elevation_ft: string;
  product_thickness_ft: string;
  /** A tenant gauging flag code (e.g. "DR") when no measurement was
   *  taken; null = a normal measured record. Mutually exclusive with the
   *  measurement fields in the UI. */
  flag_code: string | null;
  notes: string;
}

const EMPTY_FORM: FormState = {
  is_dry: false,
  depth_to_water_ft: '',
  water_elevation_ft: '',
  product_thickness_ft: '',
  flag_code: null,
  notes: '',
};

export function WizardPage() {
  const { eventId = '', wellId = '' } = useParams();
  const navigate = useNavigate();
  const gps = useGpsFix(true);

  const [envelope, setEnvelope] = useState<AssignedEventEnvelope | null>(null);
  const [planItem, setPlanItem] = useState<SamplingPlanItem | null>(null);
  const [well, setWell] = useState<CachedWellShape | null>(null);
  const [siteRadius, setSiteRadius] = useState<number | null>(null);
  const [flagCodes, setFlagCodes] = useState<CachedFlagCode[]>([]);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [photos, setPhotos] = useState<PhotoBlobRow[]>([]);
  const [photoBusy, setPhotoBusy] = useState(false);
  // Step-machine + extended draft for plan-aware capture. We keep the
  // legacy `form` state for gauging fields so the existing autosave path
  // (depth_to_water_ft, etc.) doesn't regress.
  const [stepIdx, setStepIdx] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [fieldParamReadings, setFieldParamReadings] = useState<Reading[]>([]);
  const [purgeMethod, setPurgeMethod] = useState('');
  const [purgeVolumeL, setPurgeVolumeL] = useState('');
  const [pumpRateLpm, setPumpRateLpm] = useState('');
  const [samplesCollected, setSamplesCollected] = useState<SampleCollected[]>([]);
  const [weather, setWeather] = useState('');
  const [wellCondition, setWellCondition] = useState('');
  const [odor, setOdor] = useState('');
  const [sheen, setSheen] = useState('');
  const [observations, setObservations] = useState('');

  // Reload photos whenever the active draft changes.
  useEffect(() => {
    if (!draftId) {
      setPhotos([]);
      return;
    }
    let cancelled = false;
    void listPhotos(draftId).then((rows) => {
      if (!cancelled) setPhotos(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [draftId]);

  const handleAddPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting same file
    if (!file) return;
    if (photos.length >= MAX_PHOTOS_PER_RECORD) {
      setError(`Max ${MAX_PHOTOS_PER_RECORD} photos per reading`);
      return;
    }

    // Photos need a draftId so the upload-on-sync hook (Phase 3.22b)
    // can match them to the FieldRecord. Force the draft to exist now.
    let id = draftId;
    if (!id) {
      id = await persistDraft(eventId, wellId, form, null);
      if (id) setDraftId(id);
    }
    if (!id) {
      setError('Type in any field first to start a draft');
      return;
    }

    setPhotoBusy(true);
    try {
      const fieldKey = `photo_${Date.now().toString(36)}`;
      await capturePhoto(
        id,
        fieldKey,
        file,
        gps.fix
          ? { lat: gps.fix.lat, lon: gps.fix.lon, accuracy_m: gps.fix.accuracy_m }
          : null,
      );
      const next = await listPhotos(id);
      setPhotos(next);
    } catch (err) {
      setError(`Photo capture failed: ${(err as Error).message}`);
    } finally {
      setPhotoBusy(false);
    }
  };

  const handleRemovePhoto = async (key: string) => {
    await removePhoto(key);
    if (draftId) setPhotos(await listPhotos(draftId));
  };

  // ── Hydrate from cache + draft ─────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const db = getDb();
        const [evRow, draft] = await Promise.all([
          db.cached_events.get(eventId),
          db.local_field_records
            .where(['event_id', 'well_id'])
            .equals([eventId, wellId])
            .first(),
        ]);
        if (!evRow) {
          if (!cancelled) setError('Event not in cache');
          return;
        }
        const env = evRow.data as AssignedEventEnvelope;
        const item =
          (env.plan_items as SamplingPlanItem[]).find((p) => p.well_id === wellId) ?? null;

        const siteRow = await db.cached_sites.get(env.event.site_id);
        const siteData = siteRow?.data as { geofence_radius_m?: number | null } | undefined;

        // Find the well in cached_wells. Prefer the site-scoped slice,
        // but fall back to a full scan by id — the well might be cached
        // under a different site key (or the cached_sites slice may have
        // been trimmed). If it's nowhere, that's fine: the wizard still
        // renders, it just can't do well-level geofencing.
        const wellRows = await db.cached_wells
          .where('site_id')
          .equals(env.event.site_id)
          .toArray();
        let matched = wellRows
          .map((r) => r.data as CachedWellShape)
          .find((w) => w.id === wellId);
        if (!matched) {
          const any = await db.cached_wells
            .filter((r) => (r.data as CachedWellShape)?.id === wellId)
            .first();
          matched = any ? (any.data as CachedWellShape) : undefined;
        }
        // Last resort: /field/sync/me denormalizes the well's coords +
        // screen onto the plan item, so we can still geofence even if the
        // cached_wells slice never made it to disk.
        if (!matched && item) {
          const pi = item as SamplingPlanItem & {
            latitude?: number | null;
            longitude?: number | null;
            total_depth_ft?: number | null;
            casing_elevation_ft?: number | null;
            screen_top_ft?: number | null;
            screen_bottom_ft?: number | null;
            geofence_radius_m?: number | null;
          };
          if (pi.well_name || pi.latitude != null || pi.screen_top_ft != null) {
            matched = {
              id: pi.well_id,
              name: pi.well_name ?? pi.well_id.slice(0, 8),
              latitude: pi.latitude ?? null,
              longitude: pi.longitude ?? null,
              total_depth_ft: pi.total_depth_ft ?? null,
              casing_elevation_ft: pi.casing_elevation_ft ?? null,
              screen_top_ft: pi.screen_top_ft ?? null,
              screen_bottom_ft: pi.screen_bottom_ft ?? null,
              geofence_radius_m: pi.geofence_radius_m ?? null,
            };
          }
        }

        const flagRows = await db.cached_flag_codes.toArray();

        if (cancelled) return;
        setEnvelope(env);
        setPlanItem(item);
        setWell(matched ?? null);
        // Pre-populate the sample-collection checklist from every plan item
        // at this well (one row per container instance).
        const itemsAtWell = (env.plan_items as SamplingPlanItem[]).filter(
          (p) => p.well_id === wellId,
        );
        setSamplesCollected(expandPlanItemSamples(itemsAtWell));
        setSiteRadius(siteData?.geofence_radius_m ?? null);
        setFlagCodes(
          dedupeFlagCodes(
            [...flagRows].sort((a, b) => a.code.localeCompare(b.code)),
          ),
        );

        if (draft) {
          const d = draft.data as FieldRecordUpsert;
          setDraftId(draft.client_submission_id);
          setForm({
            is_dry: d.is_dry ?? false,
            depth_to_water_ft:
              d.depth_to_water_ft != null ? String(d.depth_to_water_ft) : '',
            water_elevation_ft:
              d.water_elevation_ft != null ? String(d.water_elevation_ft) : '',
            product_thickness_ft:
              d.product_thickness_ft != null
                ? String(d.product_thickness_ft)
                : '',
            flag_code: (d.flag_code ?? null) as string | null,
            notes: d.notes ?? '',
          });
        }
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId, wellId]);

  // ── Autosave draft ────────────────────────────────────────────────
  useEffect(() => {
    if (envelope == null) return;
    const handle = setTimeout(() => {
      void persistDraft(eventId, wellId, form, draftId).then((id) => {
        if (id !== draftId) setDraftId(id);
      });
    }, 500);
    return () => clearTimeout(handle);
    // We don't include draftId in deps — it's a setter signal, not a
    // real dependency. Stable refs for envelope/planItem prevent a
    // tight loop here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, wellId, form, envelope, planItem]);

  // ── Computed fields ────────────────────────────────────────────────
  const distanceToWell = useMemo(() => {
    if (!gps.fix || well?.latitude == null || well?.longitude == null) return null;
    return haversineMeters(gps.fix, { lat: well.latitude, lon: well.longitude });
  }, [gps.fix, well]);

  const wellRadius = well?.geofence_radius_m ?? siteRadius ?? 30;
  const overGeofence =
    distanceToWell != null && distanceToWell > wellRadius;

  const validationIssues: ValidationIssue[] = useMemo(() => {
    if (!well || form.flag_code) return [];
    const wells = new Map<string, unknown>();
    wells.set(well.name.trim().toLowerCase(), {
      name: well.name,
      total_depth_ft: well.total_depth_ft ?? null,
      casing_elevation_ft: well.casing_elevation_ft ?? null,
      measuring_point_elevation_ft: well.measuring_point_elevation_ft ?? null,
      screen_top_ft: well.screen_top_ft ?? null,
    });
    return validateRowOffline(
      {
        well_name: well.name,
        depth_to_water_ft: form.depth_to_water_ft,
        data_type: 'gauging',
      },
      { rules: {}, wells, analytes: new Map() },
    );
  }, [well, form.depth_to_water_ft, form.flag_code]);

  const accuracyBucket = gpsAccuracyBucket(gps.fix?.accuracy_m);

  // Step machine driven by the plan items for this well.
  const itemsAtWell = useMemo(
    () => ((envelope?.plan_items as SamplingPlanItem[] | undefined) ?? []).filter(
      (p) => p.well_id === wellId,
    ),
    [envelope, wellId],
  );
  const steps = useMemo<StepId[]>(() => stepsForWell(itemsAtWell), [itemsAtWell]);
  const currentStep: StepId = steps[stepIdx] ?? 'gauging';
  const isFirstStep = stepIdx === 0;
  const onReview = currentStep === 'review';

  function goNext() {
    haptics.tap();
    setCompletedSteps((s) => new Set(s).add(stepIdx));
    setStepIdx((i) => Math.min(i + 1, steps.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  function goBack() {
    haptics.tap();
    setStepIdx((i) => Math.max(i - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  function jumpToStep(target: StepId) {
    const idx = steps.indexOf(target);
    if (idx >= 0) {
      haptics.tap();
      setStepIdx(idx);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  // Snapshot of the current wizard state — passed to ReviewStep + assembled
  // into the FieldRecordUpsert at submit.
  const draftSnapshot: WizardDraft = {
    is_dry: form.is_dry,
    depth_to_water_ft: form.depth_to_water_ft,
    water_elevation_ft: form.water_elevation_ft,
    product_thickness_ft: form.product_thickness_ft,
    flag_code: form.flag_code,
    field_param_readings: fieldParamReadings,
    purge_method: purgeMethod,
    purge_volume_l: purgeVolumeL,
    pump_rate_lpm: pumpRateLpm,
    samples_collected: samplesCollected,
    weather,
    well_condition: wellCondition,
    odor,
    sheen,
    observations,
    notes: form.notes,
  };

  // ── Submit ─────────────────────────────────────────────────────────
  const submit = async () => {
    if (submitting) return;
    haptics.tap();
    setSubmitting(true);
    setError(null);
    try {
      // Build the FieldRecordUpsert body. Empty strings → undefined.
      const dtw = parseOptionalFloat(form.depth_to_water_ft);
      const wel = parseOptionalFloat(form.water_elevation_ft);
      const pth = parseOptionalFloat(form.product_thickness_ft);
      // Build the extended upsert, only including the plan-aware fields when
      // they have meaningful values so we don't litter null/empty JSONB.
      const stab = fieldParamReadings.length >= 3
        ? isStabilized(fieldParamReadings)
        : { stabilized: false };
      const purgeVolNum = parseOptionalFloat(purgeVolumeL);
      const pumpRateNum = parseOptionalFloat(pumpRateLpm);
      const upsert: FieldRecordUpsert = {
        event_id: eventId,
        well_id: wellId,
        collection_date: new Date().toISOString(),
        status: 'complete',
        is_dry: form.is_dry,
        flag_code: form.flag_code ?? undefined,
        depth_to_water_ft: dtw,
        water_elevation_ft: wel,
        product_thickness_ft: pth,
        notes: form.notes.trim() || undefined,
        gps_lat: gps.fix?.lat,
        gps_lon: gps.fix?.lon,
        gps_accuracy_m: gps.fix?.accuracy_m,
        gps_distance_to_well_m: distanceToWell ?? undefined,
        gps_well_geofence_override: overGeofence,
        // Plan-aware additions (omitted when empty so the backend JSONB stays clean)
        ...(fieldParamReadings.length > 0 && {
          field_params: { readings: fieldParamReadings, stabilized: stab.stabilized },
          stabilized: stab.stabilized || undefined,
        }),
        ...(purgeMethod && { purge_method: purgeMethod }),
        ...(purgeVolNum != null && { purge_volume_l: purgeVolNum }),
        ...(pumpRateNum != null && { pump_rate_lpm: pumpRateNum }),
        ...(samplesCollected.some((s) => s.collected) && {
          samples_collected: samplesCollected.filter((s) => s.collected),
        }),
        ...(weather.trim() && { weather: weather.trim() }),
        ...(wellCondition.trim() && { well_condition: wellCondition.trim() }),
        ...(odor.trim() && { odor: odor.trim() }),
        ...(sheen.trim() && { sheen: sheen.trim() }),
        ...(observations.trim() && { observations: observations.trim() }),
      };

      // Promote draft → synced after enqueue commits.
      const id = await enqueueSubmission({
        endpoint: `/api/v1/data-vault/sampling/events/${eventId}/field-records`,
        payload: { ...upsert, client_submission_id: draftId ?? undefined },
        event_id: eventId,
      });

      // Move any draft photos onto the canonical client_submission_id.
      // (enqueueSubmission generates a new UUID; if we had a draftId
      // those photos need to migrate so the upload-on-sync hook
      // can find them.)
      if (draftId && draftId !== id) {
        await reassignPhotos(draftId, id);
      }

      // The PWA sets the local_field_record's status to 'synced' so the
      // event detail UI flips immediately to the green check; the engine
      // owns the actual server round-trip.
      await getDb().local_field_records.put({
        client_submission_id: id,
        event_id: eventId,
        well_id: wellId,
        status: 'synced',
        data: upsert,
        updated_at: Date.now(),
      });
      // Drop any older draft row keyed under a different UUID.
      if (draftId && draftId !== id) {
        await getDb().local_field_records.delete(draftId);
      }

      haptics.success();
      navigate(`/events/${eventId}`, { replace: true });
    } catch (err) {
      haptics.error();
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <p className="text-accent-red mb-4">{error}</p>
        <Link to={`/events/${eventId}`} className="btn-secondary">
          Back to event
        </Link>
      </div>
    );
  }

  if (!envelope) {
    return (
      <div className="flex-1 flex items-center justify-center p-6 text-mono-400">
        Loading…
      </div>
    );
  }

  // The well/plan-item may not be in the cached slice — render anyway so
  // the tech is never blocked; just degrade the well-specific bits.
  const wellName =
    well?.name ?? planItem?.well_name ?? `Well ${wellId.slice(0, 8)}`;

  return (
    <div className="flex-1 flex flex-col p-6 gap-6">
      <Link
        to={`/events/${eventId}`}
        className="inline-flex items-center gap-1 text-mono-400 text-sm"
      >
        <ArrowLeftIcon className="h-4 w-4" /> {envelope.event.name}
      </Link>

      <div>
        <h1 className="text-2xl font-semibold">{wellName}</h1>
        <p className="text-mono-500 text-sm">
          {planItem?.analyte_group ?? 'Gauging'}
          {planItem?.method && ` · ${planItem.method}`}
        </p>
        {!well && (
          <p className="text-accent-amber text-xs mt-1">
            Well details not in offline cache — geofencing disabled for this well.
          </p>
        )}
      </div>

      {/* Step machine — only render when there's more than the single
          gauging step; a gauge-only well just shows the original flat form. */}
      {steps.length > 1 && (
        <Stepper
          steps={steps.map((id) => ({ id, label: stepLabel(id) }))}
          current={stepIdx}
          completed={completedSteps}
          onJump={(idx) => setStepIdx(idx)}
        />
      )}

      {/* GPS section — always visible regardless of step, since it informs
          every capture. */}
      <section className="border border-mono-800 rounded-md p-3">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2 text-sm text-mono-300">
            <GpsIcon className="h-4 w-4" />
            <span>GPS fix</span>
          </div>
          <span
            className={
              accuracyBucket === 'good'
                ? 'text-accent-green text-sm'
                : accuracyBucket === 'ok'
                  ? 'text-accent-amber text-sm'
                  : 'text-accent-red text-sm'
            }
          >
            {gps.fix
              ? `±${Math.round(gps.fix.accuracy_m)} m`
              : gps.unavailable
                ? 'unavailable'
                : 'acquiring…'}
          </span>
        </div>
        {gps.fix && (
          <p className="text-mono-500 text-xs">
            {gps.fix.lat.toFixed(5)}, {gps.fix.lon.toFixed(5)}
            {distanceToWell != null && (
              <span
                className={
                  overGeofence
                    ? 'ml-2 text-accent-amber'
                    : 'ml-2 text-accent-green'
                }
              >
                {Math.round(distanceToWell)} m from well
                {overGeofence && ' (outside geofence)'}
              </span>
            )}
          </p>
        )}
        {overGeofence && (
          <div className="mt-2 text-xs text-accent-amber bg-amber-950/20 border border-amber-900/40 rounded p-2">
            GPS shows you {Math.round(distanceToWell!)}m from {wellName} (radius
            {' '}{wellRadius}m). Confirm correct well or wait for a better fix.
            Submitting will record an override for audit.
          </div>
        )}
      </section>

      {/* Plan-aware step machine. The original gauging UI (flag + measurements
          + photos + validation) is the "gauging" step. New steps render their
          dedicated components. The sticky nav bar at the bottom advances. */}
      {currentStep === 'gauging' && (
        <>
      {/* Flag — no-measurement reason (dry / no access / obstructed / …) */}
      {flagCodes.length > 0 && (
        <section className="border border-mono-800 rounded-md p-3 space-y-2">
          <div className="text-xs uppercase tracking-wider text-mono-500">
            Flag (no measurement taken)
          </div>
          <div className="flex flex-wrap gap-2">
            {flagCodes.map((fc) => {
              const selected = form.flag_code === fc.code;
              return (
                <button
                  key={fc.code}
                  type="button"
                  onClick={() =>
                    setForm((f) =>
                      f.flag_code === fc.code
                        ? { ...f, flag_code: null }
                        : {
                            ...f,
                            flag_code: fc.code,
                            is_dry: false,
                            depth_to_water_ft: '',
                            water_elevation_ft: '',
                            product_thickness_ft: '',
                          },
                    )
                  }
                  className={`min-h-touch px-3 rounded-md border text-sm ${
                    selected
                      ? 'bg-accent-amber/20 border-accent-amber text-accent-amber'
                      : 'border-mono-700 text-mono-300 active:bg-mono-900'
                  }`}
                >
                  {flagCodeLabel(fc.description, fc.code)}
                </button>
              );
            })}
          </div>
          {form.flag_code && (
            <p className="text-accent-amber text-xs">
              Flagged "
              {flagCodeLabel(
                flagCodes.find((c) => c.code === form.flag_code)?.description,
                form.flag_code,
              )}
              " — measurements skipped. Tap the button again to clear.
            </p>
          )}
        </section>
      )}

      {/* Measurements */}
      <section
        className={`space-y-4 ${
          form.flag_code ? 'opacity-50 pointer-events-none' : ''
        }`}
      >
        <div>
          <label className="block text-xs uppercase tracking-wider text-mono-500 mb-1">
            Depth to water (ft)
          </label>
          <input
            type="text"
            inputMode="decimal"
            pattern="[0-9.]*"
            className="input-field text-lg"
            value={form.depth_to_water_ft}
            onChange={(e) =>
              setForm((f) => ({ ...f, depth_to_water_ft: e.target.value }))
            }
          />
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wider text-mono-500 mb-1">
            Water elevation (ft MSL)
          </label>
          <input
            type="text"
            inputMode="decimal"
            pattern="[0-9.]*"
            className="input-field text-lg"
            value={form.water_elevation_ft}
            onChange={(e) =>
              setForm((f) => ({ ...f, water_elevation_ft: e.target.value }))
            }
          />
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wider text-mono-500 mb-1">
            Product thickness (ft)
          </label>
          <input
            type="text"
            inputMode="decimal"
            pattern="[0-9.]*"
            className="input-field text-lg"
            value={form.product_thickness_ft}
            onChange={(e) =>
              setForm((f) => ({ ...f, product_thickness_ft: e.target.value }))
            }
          />
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wider text-mono-500 mb-2">
            Photos ({photos.length} / {MAX_PHOTOS_PER_RECORD})
          </label>
          {photos.length > 0 && (
            <ul className="grid grid-cols-3 gap-2 mb-2">
              {photos.map((p) => (
                <PhotoThumb
                  key={p.key}
                  photo={p}
                  onRemove={() => handleRemovePhoto(p.key)}
                />
              ))}
            </ul>
          )}
          <label
            className={`btn-secondary inline-block w-full text-center cursor-pointer ${
              photos.length >= MAX_PHOTOS_PER_RECORD || photoBusy
                ? 'pointer-events-none opacity-50'
                : ''
            }`}
          >
            {photoBusy ? 'Compressing…' : '+ Add photo'}
            <input
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={handleAddPhoto}
              disabled={photos.length >= MAX_PHOTOS_PER_RECORD || photoBusy}
            />
          </label>
          <p className="text-mono-500 text-xs mt-1">
            Compressed to ~300 KB each, stored offline until sync.
          </p>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wider text-mono-500 mb-1">
            Notes
          </label>
          <textarea
            rows={3}
            className="input-field"
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          />
        </div>
      </section>

      {/* Validation warnings */}
      {validationIssues.length > 0 && (
        <section className="space-y-2">
          {validationIssues.map((issue, i) => (
            <div
              key={i}
              className="text-xs border rounded p-2 border-amber-900/40 bg-amber-950/20 text-accent-amber"
            >
              ⚠ {issue.message}
            </div>
          ))}
        </section>
      )}
        </>
      )}

      {/* Plan-aware steps */}
      {currentStep === 'field-params' && (
        <FieldParamsStep
          readings={fieldParamReadings}
          setReadings={setFieldParamReadings}
        />
      )}
      {currentStep === 'purge' && (
        <PurgeStep
          lowFlow={itemsAtWell.some((i) => (i as unknown as { low_flow?: boolean }).low_flow)}
          purgeMethod={purgeMethod} setPurgeMethod={setPurgeMethod}
          purgeVolumeL={purgeVolumeL} setPurgeVolumeL={setPurgeVolumeL}
          pumpRateLpm={pumpRateLpm} setPumpRateLpm={setPumpRateLpm}
        />
      )}
      {currentStep === 'samples' && (
        <SamplesStep
          items={itemsAtWell.filter((i) => !(i as unknown as { gauge_only?: boolean }).gauge_only)}
          samples={samplesCollected}
          setSamples={setSamplesCollected}
        />
      )}
      {currentStep === 'observations' && (
        <ObservationsStep
          weather={weather} setWeather={setWeather}
          wellCondition={wellCondition} setWellCondition={setWellCondition}
          odor={odor} setOdor={setOdor}
          sheen={sheen} setSheen={setSheen}
          observations={observations} setObservations={setObservations}
          notes={form.notes} setNotes={(v) => setForm((f) => ({ ...f, notes: v }))}
        />
      )}
      {currentStep === 'review' && (
        <ReviewStep
          draft={draftSnapshot}
          steps={steps}
          onJump={jumpToStep}
        />
      )}

      {/* Sticky nav — Back / Next on most steps; Submit only on Review. */}
      <div className="sticky bottom-0 -mx-4 px-4 pt-3 pb-safe-4 bg-mono-950/95 backdrop-blur border-t border-mono-800 flex items-center gap-2">
        <button
          type="button"
          onClick={goBack}
          disabled={isFirstStep}
          className="inline-flex items-center gap-1 min-h-touch px-3 py-2 rounded-md border border-mono-700 text-mono-200 active:bg-mono-800 disabled:opacity-40"
        >
          <ChevronLeft className="h-4 w-4" /> Back
        </button>
        <div className="flex-1 text-center text-[11px] text-mono-500">
          Step {stepIdx + 1} of {steps.length}
        </div>
        {onReview ? (
          <button
            type="button"
            className="btn-primary inline-flex items-center justify-center gap-1.5 !w-auto !px-5"
            disabled={submitting}
            onClick={submit}
          >
            <Send className="h-4 w-4" />
            {submitting ? 'Saving…' : 'Save reading'}
          </button>
        ) : (
          <button
            type="button"
            onClick={goNext}
            className="btn-primary inline-flex items-center justify-center gap-1 !w-auto !px-4"
          >
            Next <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function PhotoThumb({
  photo,
  onRemove,
}: {
  photo: PhotoBlobRow;
  onRemove: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const u = URL.createObjectURL(photo.blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [photo.blob]);

  return (
    <li className="relative aspect-square overflow-hidden rounded border border-mono-800">
      {url && (
        <img
          src={url}
          alt="Field capture"
          className="h-full w-full object-cover"
        />
      )}
      <button
        type="button"
        onClick={onRemove}
        /* Promoted to 44×44 touch target per Apple HIG. */
        className="absolute top-1 right-1 h-11 w-11 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center active:scale-95 transition-transform"
        aria-label="Remove photo"
      >
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M3 6h18" />
          <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          <path d="M19 6 18 20a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        </svg>
      </button>
      <span className="absolute bottom-0 inset-x-0 px-1 py-0.5 bg-black/60 text-mono-200 text-[10px]">
        {Math.round(photo.blob.size / 1024)} KB
      </span>
    </li>
  );
}

function parseOptionalFloat(s: string): number | undefined {
  const t = s.trim();
  if (!t) return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
}

async function persistDraft(
  eventId: string,
  wellId: string,
  form: FormState,
  existingId: string | null,
): Promise<string | null> {
  // Don't autosave a completely empty form — wait until the user types
  // something, otherwise opening the wizard creates a junk draft.
  const isPristine =
    !form.is_dry &&
    !form.flag_code &&
    !form.depth_to_water_ft &&
    !form.water_elevation_ft &&
    !form.product_thickness_ft &&
    !form.notes.trim();
  if (isPristine && existingId == null) return null;

  const id =
    existingId ??
    (typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

  const data: FieldRecordUpsert = {
    event_id: eventId,
    well_id: wellId,
    collection_date: new Date().toISOString(),
    status: 'pending',
    is_dry: form.is_dry,
    flag_code: form.flag_code ?? undefined,
    depth_to_water_ft: parseOptionalFloat(form.depth_to_water_ft),
    water_elevation_ft: parseOptionalFloat(form.water_elevation_ft),
    product_thickness_ft: parseOptionalFloat(form.product_thickness_ft),
    notes: form.notes.trim() || undefined,
    client_submission_id: id,
  };

  await getDb().local_field_records.put({
    client_submission_id: id,
    event_id: eventId,
    well_id: wellId,
    status: 'draft',
    data,
    updated_at: Date.now(),
  });
  return id;
}
